<script setup lang="ts">
import { usePartialComponent } from '@precon-tools-suite-by-shared/vue-partials';
import { useDivisibleReceiver } from '@precon-tools-suite-by-shared/vue-divisibles';
import Skeleton from 'primevue/skeleton';
import { mergeProps, type Component } from 'vue';

const props = withDefaults(
    defineProps<{
        /**
         * Component to render the title.
         */
        titleIs?: string | Component;
        /**
         * The partial name or component to render in this receiver.
         */
        partial?: string | Component | null;
        /**
         * The fallback partial to show when the `partial`-item doesn't exist.
         */
        fallbackPartial?: string | Component | null;
        /**
         * The properties thaat will be passed to the partial rendered in this receiver.
         */
        partialProps?: Record<string, unknown>;
    }>(),
    {
        titleIs: 'h3',
        partial: null,
        fallbackPartial: null,
        partialProps: () => ({}),
    }
);

const component = usePartialComponent(() => props.partial, {
    fallback: () => props.fallbackPartial,
});

const receiver = useDivisibleReceiver();

const { component: HeaderPart, exists: headerExists } = receiver.use('header');

const { component: TitlePart } = receiver.use('title');

const { component: SubtitlePart } = receiver.use('subtitle');

const { component: DescriptionPart, exists: descriptionExists } =
    receiver.use('description');

const { component: ToolsPart } = receiver.use('tools');
const { component: FooterPart } = receiver.use('footer');

const { component: ActionsPart } = receiver.use('actions');
</script>

<template>
    <article>
        <header class="flex">
            <div class="flex-grow-1">
                <template v-if="headerExists">
                    <HeaderPart />
                </template>
                <component :is="titleIs" v-else class="mb-1">
                    <TitlePart>
                        <template #skeleton>
                            <Skeleton
                                style="
                                    height: 2em;
                                    width: 12em;
                                    display: inline-block;
                                "
                            />
                        </template>
                    </TitlePart>
                    <span class="ml-4 text-xl" style="opacity: 0.7">
                        <SubtitlePart>
                            <template #skeleton>
                                <Skeleton
                                    style="
                                        height: 1.4em;
                                        width: 18em;
                                        display: inline-block;
                                    "
                                />
                            </template>
                        </SubtitlePart>
                    </span>
                </component>
            </div>
            <ToolsPart>
                <template #skeleton>
                    <Button
                        class="p-button-rounded p-button-text p-button-secondary"
                        icon="pi pi-spinner pi-spin"
                        disabled
                    />
                </template>
            </ToolsPart>
        </header>

        <p v-if="descriptionExists" class="text-lg text-gray-500 p-2">
            <DescriptionPart>
                <template #skeleton>
                    <Skeleton style="max-width: 70%" />
                </template>
            </DescriptionPart>
        </p>
        <div class="card mb-2" :class="{ 'mt-2': !descriptionExists }">
            <div class="card-body">
                <component
                    :is="component"
                    v-bind="mergeProps($attrs, partialProps)"
                >
                    <slot />
                </component>
            </div>
        </div>
        <footer
            class="flex px-4 py-2 align-items-center justify-content-end gap-2"
        >
            <div class="mr-auto">
                <FooterPart>
                    <template #skeleton>
                        <Skeleton
                            style="
                                width: 7em;
                                max-width: 50%;
                                margin-botton: 0.25rem;
                            "
                        />
                        <Skeleton style="width: 14rem; max-width: 80%" />
                    </template>
                </FooterPart>
            </div>
            <ActionsPart>
                <template #skeleton>
                    <Skeleton style="height: 2.2rem; width: 8rem" />
                    <Skeleton style="height: 2.2rem; width: 6rem" />
                </template>
            </ActionsPart>
        </footer>
    </article>
</template>
